import React, { useState } from "react";
import PropTypes from "prop-types";
import { useWatch, useFormContext } from "react-hook-form";
import classnames from "classnames";

import Label from "../Label";
import { FormControl } from "../Form";
import FormInput from "../FormInput";
import FormHelper from "../FormHelper";
import Icon from "../../Icon";

import { isValidPassword } from "../../../helpers";
import { PASSWORD_MIN_LENGTH } from "../../../constants";

import "./passwordInput.scss";

const PasswordInput = ({
  name,
  label,
  options,
  autoComplete,
  className,
  showForgotPassword,
  shouldValidatePassword,
  matchPassword,
}) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const currentValue = useWatch({
    control,
    name,
  });
  const [isFocused, setIsFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const hasValue = typeof currentValue !== "undefined" && currentValue !== "";
  const currentError = errors[name];

  const handleFocus = () => {
    setIsFocused(!isFocused);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleTogglePassword = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const validatePassword = (password) => {
    if (!shouldValidatePassword) return true;
    if (matchPassword) {
      return password === matchPassword ? true : "Passwords Do not Match.";
    }
    return password.length >= PASSWORD_MIN_LENGTH && isValidPassword(password)
      ? true
      : "Invalid Password.";
  };

  return (
    <FormControl>
      <FormInput
        type="password"
        className={classnames(className, {
          "has-value": hasValue,
          "has-error": currentError,
          "is-active": isFocused,
          "is-disabled": options?.disabled,
        })}
      >
        <Label
          htmlFor={name}
          text={label}
          required={options?.required}
          showRequiredAsterisk={true}
        />
        <input
          id={name}
          className={classnames({ [`text-input--${name}`]: name })}
          type={!isPasswordVisible ? "password" : "text"}
          autoComplete={autoComplete}
          disabled={options?.disabled}
          aria-invalid={!!currentError}
          placeholder={options?.placeholder}
          onFocus={handleFocus}
          {...register(name, {
            ...options,
            onBlur: handleBlur,
            validate: validatePassword,
          })}
        />

        {hasValue && (
          <button
            type="button"
            className="loginPassword-eye"
            aria-label="Show Password"
            onClick={handleTogglePassword}
          >
            <Icon
              name={
                !isPasswordVisible ? "system-view-off" : "system-view-filled"
              }
              size="medium"
            />
          </button>
        )}
      </FormInput>
      {showForgotPassword && (
        <a
          href="/request-password"
          className="form-helper form-helper--password"
        >
          Forgot password?
        </a>
      )}
      {currentError && <FormHelper error={currentError} label={label} />}
    </FormControl>
  );
};

PasswordInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.object,
  className: PropTypes.string,
  autoComplete: PropTypes.string,
  showForgotPassword: PropTypes.bool,
  shouldValidatePassword: PropTypes.bool,
  matchPassword: PropTypes.string,
};

PasswordInput.defaultProps = {
  options: {},
  autoComplete: "off",
  showForgotPassword: true,
  shouldValidatePassword: false,
};

export default PasswordInput;
